import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { calculateBillTotals } from "../utils/accountsHelpers";

const emptyItem = {
  description: "",
  quantity: 1,
  rate: 0,
  taxRate: 0,
};

const BillForm = ({ initialData, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    vendorId: "",
    billDate: "",
    dueDate: "",
    vendorSnapshot: { name: "" },
    items: [emptyItem],
    notes: "",
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        vendorId: initialData.vendorId || "",
        billDate: initialData.billDate || "",
        dueDate: initialData.dueDate || "",
        vendorSnapshot: initialData.vendorSnapshot || { name: "" },
        items: initialData.items?.length ? initialData.items : [emptyItem],
        notes: initialData.notes || "",
      });
    }
  }, [initialData]);

  const handleItemChange = (index, key, value) => {
    const next = [...formData.items];
    next[index] = { ...next[index], [key]: value };
    setFormData((prev) => ({ ...prev, items: next }));
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { ...emptyItem }],
    }));
  };

  const removeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const totals = calculateBillTotals(formData.items);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(formData);
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Vendor ID"
            value={formData.vendorId}
            onChange={(e) =>
              setFormData((p) => ({ ...p, vendorId: e.target.value }))
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Vendor Name"
            value={formData.vendorSnapshot.name}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                vendorSnapshot: { ...p.vendorSnapshot, name: e.target.value },
              }))
            }
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Bill Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.billDate}
            onChange={(e) =>
              setFormData((p) => ({ ...p, billDate: e.target.value }))
            }
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Due Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={formData.dueDate}
            onChange={(e) =>
              setFormData((p) => ({ ...p, dueDate: e.target.value }))
            }
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1" fontWeight={600}>
            Bill Items
          </Typography>
        </Grid>

        {formData.items.map((item, index) => (
          <React.Fragment key={index}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Description"
                value={item.description}
                onChange={(e) =>
                  handleItemChange(index, "description", e.target.value)
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={4} md={2}>
              <TextField
                label="Qty"
                type="number"
                value={item.quantity}
                onChange={(e) =>
                  handleItemChange(index, "quantity", Number(e.target.value))
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={4} md={2}>
              <TextField
                label="Rate"
                type="number"
                value={item.rate}
                onChange={(e) =>
                  handleItemChange(index, "rate", Number(e.target.value))
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={3} md={2}>
              <TextField
                label="Tax %"
                type="number"
                value={item.taxRate}
                onChange={(e) =>
                  handleItemChange(index, "taxRate", Number(e.target.value))
                }
                fullWidth
              />
            </Grid>
            <Grid
              item
              xs={1}
              md={2}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <IconButton color="error" onClick={() => removeItem(index)}>
                <Delete />
              </IconButton>
            </Grid>
          </React.Fragment>
        ))}

        <Grid item xs={12}>
          <Button startIcon={<Add />} onClick={addItem}>
            Add Item
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Typography>Gross: {totals.grossAmount}</Typography>
          <Typography>Tax: {totals.taxTotal}</Typography>
          <Typography fontWeight={700}>
            Grand Total: {totals.grandTotal}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Notes"
            value={formData.notes}
            onChange={(e) =>
              setFormData((p) => ({ ...p, notes: e.target.value }))
            }
            fullWidth
            multiline
            rows={3}
          />
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
        <Button type="submit" variant="contained" disabled={loading}>
          {initialData ? "Update Bill" : "Create Bill"}
        </Button>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
      </Box>
    </form>
  );
};

export default BillForm;
